import { Box, Button } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { DocumentReferenceResponse, GetProjectResponse } from "api/generatedApi";
import { api } from "api/enhancedEndpoints";
import { ScreenSizeEnum, useScreenSize } from "shared/use-screen-size";
import { useToast } from "shared/toast/hooks/use-toast";
import { useConfirm } from "components/shared/alert/confirm/hooks/use-confirm";
import { DesktopThreeStepNavigation } from "components/shared/three-step-navigation/desktop-three-step-navigation";
import { ExtraWorkAgreementFormData, useValidateExtraWorkAgreementInput } from "./hooks/use-input-validation";
import { useCreateExtraWorkAgreement } from "./hooks/use-create-extra-work-agreement";
import { useExtraWorkAgreementPermissions } from "./hooks/use-extra-work-agreement-permissions";
import { MobileOneStepBackBreadcrumb } from "./inputs/mobile-one-step-back";
import { DescriptionCard } from "./inputs/descriptionCard";
import { ExtraWACardWithHeader } from "./inputs/cardWithHeader";
import { DocumentCard } from "./inputs/documentCard";
import { DetailCard } from "./inputs/detailCard";
import { EditExtraWorkAgreementStatus } from "./inputs/agreement-status";
import { EditExtraWorkAgreementName } from "./inputs/name";

interface Props {
  project: GetProjectResponse;
}

export const ExtraWorkAgreementCreatePage = (props: Props) => {
  const { project } = props;
  const { t } = useTranslation();
  const { screenSize } = useScreenSize();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();

  const [tempFilesforUpload, setTempFilesforUpload] = useState<File[]>([]);
  const [tempFilesForDelete, setTempFilesForDelete] = useState<DocumentReferenceResponse[]>([]);

  const { hasValidateInputs } = useValidateExtraWorkAgreementInput();
  const permissions = useExtraWorkAgreementPermissions({ project });

  const createExtraWorkAgreement = useCreateExtraWorkAgreement({ project, tempFilesforUpload });

  const { register, setValue, getValues, handleSubmit, control, watch, formState } =
    useForm<ExtraWorkAgreementFormData>({
      mode: "all",
      defaultValues: {
        extraWorkAgreementType: "CustomerHours",
        workTime: { hours: 0, minutes: 0 },
        approvalStatus: "UnderDevelopment",
      },
    });

  // Subscribe to all field changes so isInputValid / isDirty recompute each render.
  watch();

  const isInputValid = hasValidateInputs(getValues);
  const isDirty =
    formState.isDirty || tempFilesforUpload.length > 0 || tempFilesForDelete.length > 0;
  const canSubmit = isInputValid && isDirty;

  const openCancelConfirm = useConfirm({
    title: t("dashboard.extraWorkAgreements.create.confirmCancelTitle"),
    description: t("dashboard.extraWorkAgreements.create.confirmCancelDescription"),
    submitButtonLabel: t("common.finish"),
    submit: () => navigate(-1),
  });

  const onCancel = () => {
    if (isDirty) {
      openCancelConfirm();
    } else {
      navigate(-1);
    }
  };

  const submit = async (
    data: ExtraWorkAgreementFormData,
    initialStatus: "UnderDevelopment" | "AwaitingApproval",
  ) => {
    if (!permissions.canCreate) {
      toast.error(t("dashboard.extraWorkAgreements.create.noPermission"));
      return;
    }
    await createExtraWorkAgreement(data, initialStatus);
    // Document uploads for the new agreement are handled inside
    // useCreateExtraWorkAgreement's onSuccess, where the new id is known.
    setTempFilesforUpload([]);
    setTempFilesForDelete([]);
    if (initialStatus === "AwaitingApproval") {
      dispatch(api.util.invalidateTags(["ExtraWorkAgreement"]));
    }
    navigate(-1);
  };

  const onSubmitCreate: SubmitHandler<ExtraWorkAgreementFormData> = (data) =>
    submit(data, "UnderDevelopment");
  const onSubmitCreateAndSend: SubmitHandler<ExtraWorkAgreementFormData> = (data) =>
    submit(data, "AwaitingApproval");

  return (
    <Box
      sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: screenSize === ScreenSizeEnum.Mobile ? 0 : 2,
          p: screenSize !== ScreenSizeEnum.LargeDesktop ? 2 : 8,
          backgroundColor: "grey",
          overflowY: "auto",
          height: "100%",
        }}
    >
      <Box>
        {screenSize === ScreenSizeEnum.Mobile ? (
          <MobileOneStepBackBreadcrumb
            from={t("dashboard.extraWorkAgreements.create.breadcrumbTitle2")}
            isDirty={isDirty}
            onClick={onCancel}
          />
        ) : (
          <DesktopThreeStepNavigation
            firstTitle={t("dashboard.extraWorkAgreements.create.breadcrumbTitle1")}
            secondTitle={t("dashboard.extraWorkAgreements.create.breadcrumbTitle2")}
            thirdTitle={t("dashboard.extraWorkAgreements.create.breadcrumbTitle3")}
            onClick={onCancel}
            isDirty={isDirty}
            labelVariant="h5"
          />
        )}
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: screenSize === ScreenSizeEnum.LargeDesktop ? "row" : "column-reverse",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", flex: 3, gap: 2 }}>
            {screenSize === ScreenSizeEnum.LargeDesktop && (
              <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.titleCardTitle")}>
                <EditExtraWorkAgreementName control={control} disabled={false} />
              </ExtraWACardWithHeader>
            )}

            <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.descriptionCardTitle")}>
              <DescriptionCard control={control} disabled={false} />
            </ExtraWACardWithHeader>

            <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.documentationCardTitle")}>
              <DocumentCard
                disabled={false}
                setTempFilesForDelete={setTempFilesForDelete}
                setTempFilesforUpload={setTempFilesforUpload}
                canEdit={permissions.canCreate}
                documents={[]}
                projectId={project.id ?? ""}
                pendingUploads={tempFilesforUpload}
              />
            </ExtraWACardWithHeader>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", flex: 2, gap: 2 }}>
            {screenSize !== ScreenSizeEnum.LargeDesktop && (
              <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.titleCardTitle")}>
                <EditExtraWorkAgreementName control={control} disabled={false} />
              </ExtraWACardWithHeader>
            )}

            <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.statusCardTitle")}>
              <EditExtraWorkAgreementStatus disabled={false} register={register} watch={watch} />
            </ExtraWACardWithHeader>

            <ExtraWACardWithHeader title={t("dashboard.extraWorkAgreements.create.detailsCardTitle")}>
              <DetailCard
                disabled={false}
                register={register}
                getValue={getValues}
                setValue={setValue}
                control={control}
                watch={watch}
                editPageState="edit"
              />
            </ExtraWACardWithHeader>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: "flex", flexDirection: "row", gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          disabled={!canSubmit}
          onClick={handleSubmit(onSubmitCreate)}
          sx={{ width: "auto" }}
        >
          {t("dashboard.extraWorkAgreements.create.createButton")}
        </Button>
        {permissions.canSendToApproval && (
          <Button
            variant="contained"
            color="primary"
            disabled={!canSubmit}
            onClick={handleSubmit(onSubmitCreateAndSend)}
            sx={{ width: "auto" }}
          >
            {t("dashboard.extraWorkAgreements.create.createAndSendButton")}
          </Button>
        )}
        <Button variant="outlined" color="primary" onClick={onCancel} sx={{ width: "auto" }}>
          {t("dashboard.extraWorkAgreements.create.cancelButton")}
        </Button>
      </Box>

      {screenSize === ScreenSizeEnum.Mobile && <Box sx={{ height: "60px" }} />}
    </Box>
  );
};
